import {FetchResult, gql, RefetchQueryDescriptor, useMutation} from '@apollo/client';
import {getYear} from 'date-fns';
import moment from 'moment';
import {useCallback} from 'react';
import {ContactAttribution} from '../pages/PrecursorSummaryPage/headerFields';
import {precursorAndHeaderFieldsType} from '../pages/PrecursorWizard/fields/precursorFields';

import {REVIEW_DETAILS_FRAGMENT} from '../graphql/fragments';
import {ReviewDetailsFragment} from '../schema-types/ReviewDetailsFragment';
import {UpdateReview, UpdateReviewVariables} from '../schema-types/UpdateReview';
import {
  ClientCommunicationAttemptInputType,
  ReviewAction,
  ReviewForEnum,
} from '../schema-types/globalTypes';
import {cleanForCuro} from '../utils/cleanString';
import {newUTCDate} from '../utils/newUTCDate';
import {REVIEW_VALIDATION} from '../pages/HandoverSummaryPage/useReviewValidation';
import {ReviewDetailsVariables} from '../schema-types/ReviewDetails';
import {SUBMIT_REVIEW} from '../pages/PrecursorSummaryPage/precursorSummaryPageQueries';
import {SubmitReview, SubmitReviewVariables} from '../schema-types/SubmitReview';
import validateUUID from '../utils/validateUUID';
import {useAccountContext, useAccountFull} from '../contexts/AccountContext';
import {AccountFull_accountDetails} from '../schema-types/AccountFull';
import {numbersToPhrasing} from '../pages/PrecursorWizard/fields/reviewAction';

export const getReviewAction = (
  values: precursorAndHeaderFieldsType
): ReviewAction | undefined => {
  if (values.reviewAction === ReviewAction.NO_RESPONSE) {
    if (getClientCommunicationAttempts(values).length >= numbersToPhrasing.length) {
      // only send NO_RESPONSE_FROM_CLIENT if its the final time
      return ReviewAction.NO_RESPONSE;
    }
    return undefined;
  }
  return values.reviewAction;
};

const NO_RESPONSE_FIELDS = [
  {date: 'noResponseDate_1', method: 'noResponseMethod_1'},
  {date: 'noResponseDate_2', method: 'noResponseMethod_2'},
  {date: 'noResponseDate_3', method: 'noResponseMethod_3'},
] as const;

export const getClientCommunicationAttempts = (
  values: precursorAndHeaderFieldsType
): ClientCommunicationAttemptInputType[] => {
  if (
    values.reviewAction === ReviewAction.CLIENT_DECLINED &&
    values.declinedByClientDate
  ) {
    return [
      {
        contactDate: moment.utc(values.declinedByClientDate).format('YYYY-MM-DD'),
        contactMethod: values.declinedByClientMethod,
        declineReason: values.declineComment || null,
      },
    ];
  }

  const attempts = NO_RESPONSE_FIELDS.filter(({date}) => values[date] != null).map(
    ({date, method}) => ({
      contactDate: moment.utc(values[date]).format('YYYY-MM-DD'),
      contactMethod: values[method],
    })
  );

  return (attempts.length > 0 ? attempts : values.previousCommunications).splice(0, 3);
};

type updateReviewOptions = {
  inPrecursor: boolean;
};

export default function useUpdatePrecursor({
  onError,
  onSuccess,
  reviewId,
  refetchQueries,
}: {
  onSuccess?: (reviewDetails: ReviewDetailsFragment) => void;
  onError?: (error?: string) => void;
  reviewId: string;
  refetchQueries?: RefetchQueryDescriptor[];
}): {
  updateReview: (
    values: precursorAndHeaderFieldsType,
    options: updateReviewOptions
  ) => Promise<FetchResult<UpdateReview>>;
  loading: boolean;
} {
  const [submitReview] = useMutation<SubmitReview, SubmitReviewVariables>(SUBMIT_REVIEW);
  const reviewDetailsVariables: ReviewDetailsVariables = {
    valueId: reviewId,
  };
  const {accountFull} = useAccountFull();
  const [_updateReview, {loading}] = useMutation<UpdateReview, UpdateReviewVariables>(
    UPDATE_REVIEW,
    {
      refetchQueries,
      //  || [
      //   {
      //     query: REVIEW_VALIDATION,
      //     variables: reviewDetailsVariables,
      //   },
      // ],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        if (data.updateReview?.ok) {
          onSuccess?.(data.updateReview.review!);
        }
        if (!data.updateReview?.ok) {
          onError?.(data.updateReview?.errors?.join(','));
        }
      },
      onError: (error) => {
        if (error.networkError) {
          onError?.('Network error');
        } else {
          onError?.(error.message);
        }
      },
    }
  );
  const updateReview = useCallback(
    async (
      values: precursorAndHeaderFieldsType,
      precursorOptions: updateReviewOptions
    ) => {
      /**
       * annualReview is a boolean flag needed to make the back-end add the necessary attrubutes
       */

      const annualReview = values.reviewReason === 'Ongoing client service';
      const plannedOrScheduledDate =
        values.plannedMeetingDate || values.bookedMeetingDate || values.pastMeetingDate;
      const {contactId, reviewFor} = getContactIdAndReviewFor(values, accountFull);
      const opportunityValue = (values.opportunityValue || '0')
        .toString()
        .replace('£', '');

      const reviewAction = getReviewAction(values);

      // if the review doesn't have a booked date and
      const reviewReadyForHeader =
        getBookedDate(values) !== null ||
        reviewAction === ReviewAction.CLIENT_DECLINED ||
        reviewAction === ReviewAction.NO_RESPONSE;

      const precursorOnlyVariables: Partial<UpdateReviewVariables> = {
        reviewId: validateUUID(reviewId)!,
        reviewReason: getReviewReason(values),
        contactId: validateUUID(contactId),
        reviewFor,
        focus: getFocus(values),
        status: getReviewStatus(values),
        dateBooked: getBookedDate(values),
        dateScheduled: getScheduledDate(values),
        // eslint-disable-next-line no-nested-ternary
        name: values.name
          ? cleanForCuro(values.name)
          : annualReview
          ? `Annual Review ${getYear(newUTCDate(plannedOrScheduledDate))}`
          : null,
        annualReview,
        meetingType: getMeetingType(values),
        precursorNotFinished: !reviewReadyForHeader,
        reviewAction,
        clientCommunicationAttempts: getClientCommunicationAttempts(values),
        adviserId: values.reviewAdviser || undefined,
      };

      const variables: UpdateReviewVariables = {
        reviewId: validateUUID(reviewId)!,
        opportunityValue: opportunityValue || 0,
        isInJourney: true,
        ...(precursorOptions.inPrecursor ? precursorOnlyVariables : {}),
      };

      // if (reviewId) { // now handled in the updateReview mutation above
      //   await setIsJourneyToTrue({
      //     variables: {
      //       reviewId,
      //     },
      //   });
      // }
      const res = await _updateReview({
        variables,
      });

      const shouldSubmitReview =
        !values.reviewAction ||
        values.reviewAction === ReviewAction.REVIEW_MEETING ||
        values.reviewAction === ReviewAction.CLIENT_DECLINED ||
        (precursorOnlyVariables.clientCommunicationAttempts?.length || 0) >= 3;
      if (shouldSubmitReview) {
        await submitReview({
          variables: {
            reviewId: validateUUID(reviewId)!,
          },
        });
      }
      return res;
    },
    [_updateReview, accountFull]
  );

  return {updateReview, loading};
}

export const getFocus = (values: precursorAndHeaderFieldsType): string | null => {
  // you would expect a boolean here but you would be wrong...
  if (values.isReviewFocused === 'No') {
    // making sure the focus is Holistic
    values.reviewFocus = 'Holistic';
    return values.reviewFocus;
  }
  return values.reviewFocus ?? null;
};

interface AccountContactOwnership {
  contactId?: string;
  reviewFor: ReviewForEnum;
}

export const getContactIdAndReviewFor = (
  values: Pick<precursorAndHeaderFieldsType, 'reviewContact'>,
  account?: Pick<AccountFull_accountDetails, 'accountType'>
): AccountContactOwnership => {
  if (account?.accountType !== 'Household' || !values?.reviewContact) {
    return {
      contactId: undefined,
      reviewFor: ReviewForEnum.ACCOUNT,
    };
  }

  if (values.reviewContact === ContactAttribution.both) {
    return {
      contactId: undefined,
      reviewFor: ReviewForEnum.JOINT,
    };
  }

  return {
    contactId: values.reviewContact,
    reviewFor: ReviewForEnum.SINGLE,
  };
};

export const useContactIdAndReviewFor = (
  values: Pick<precursorAndHeaderFieldsType, 'reviewContact'>
) => {
  const {account} = useAccountContext();
  const {reviewFor, contactId} = getContactIdAndReviewFor(values, account);

  return {reviewFor, contactId};
};

export const getMeetingType = (values: precursorAndHeaderFieldsType): string | null => {
  if (values.meetingType === 'Zoom/Teams') {
    return 'Web';
  }
  return (values.meetingType as string) ?? null;
};

export const getBookedDate = (values: precursorAndHeaderFieldsType): string | null => {
  if (values.pastMeetingDate) {
    return moment.utc(values.pastMeetingDate).toISOString();
  }
  if (values.bookedMeetingDate) {
    const str = `${values.bookedMeetingDate} ${values.bookedMeetingTime ?? ''}`;
    return moment.utc(str).toISOString();
  }
  return null;
};

export const getScheduledDate = (
  values: precursorAndHeaderFieldsType
): string | undefined => {
  if (values.plannedMeetingDate) {
    return moment.utc(values.plannedMeetingDate).toISOString();
  }
  return undefined;
};

export const getReviewReason = (values: precursorAndHeaderFieldsType): string | null => {
  if (
    values.reviewFocus === 'Consolidation' &&
    values.reviewReason !== 'Initial client meeting'
  ) {
    return 'Review of past advice';
  }
  return values.reviewReason;
};

export const getReviewStatus = (values: precursorAndHeaderFieldsType): string | null => {
  if (values.reviewAction === ReviewAction.CLIENT_DECLINED) {
    return 'Declined by client';
  }
  if (
    values.reviewAction === ReviewAction.NO_RESPONSE &&
    getClientCommunicationAttempts(values).length >= numbersToPhrasing.length
  ) {
    return 'Client uncontactable';
  }
  if (values.pastMeetingDate) {
    return 'Current';
  }
  if (values.bookedMeetingDate) {
    return 'Meeting booked';
  }
  if (values.plannedMeetingDate) {
    return 'Planned';
  }
  return null;
};

export const UPDATE_REVIEW = gql`
  mutation UpdateReview(
    $reviewId: UUID!
    $contactId: UUID
    $name: String
    $reviewReason: String
    $curoId: UUID
    $date: DateTime
    $performanceReviewProvided: Boolean
    $performanceReviewDate: DateTime
    $compliantCase: Boolean
    $atrRequested: Boolean
    $reasonNoAtr: String
    $taxYearEnd: Boolean
    $dateBooked: DateTime
    $dateScheduled: DateTime
    $status: String
    $annualReview: Boolean
    $meetingType: String
    $opportunityValue: Decimal
    $conversionClient: Boolean
    $noAttendeeReason: String
    $executionOnly: Boolean
    $focus: String
    $isInJourney: Boolean
    $precursorNotFinished: Boolean
    $postcursorNotFinished: Boolean
    $clientCommunicationAttempts: [ClientCommunicationAttemptInputType]
    $reviewAction: ReviewAction
    $isSubmittedFromOneview: Boolean
    $sendForSignoff: Boolean
    $juniorAdviserId: UUID
    $adviceAction: ReviewAdviceActionEnum
    $signoffRejectedReason: String
    $signoffApprovalReason: String
    $padReviewerId: UUID
    $adviserId: UUID
    $giaHasLiquidityNeeds: Boolean
    $pensionHasLiquidityNeeds: Boolean
    $accountSubmissionNeeded: Boolean
    $reviewFor: ReviewForEnum
  ) {
    updateReview(
      reviewId: $reviewId
      contactId: $contactId
      name: $name
      reviewReason: $reviewReason
      curoId: $curoId
      date: $date
      performanceReviewProvided: $performanceReviewProvided
      performanceReviewDate: $performanceReviewDate
      compliantCase: $compliantCase
      atrRequested: $atrRequested
      reasonNoAtr: $reasonNoAtr
      taxYearEnd: $taxYearEnd
      dateBooked: $dateBooked
      dateScheduled: $dateScheduled
      status: $status
      annualReview: $annualReview
      meetingType: $meetingType
      opportunityValue: $opportunityValue
      conversionClient: $conversionClient
      noAttendeeReason: $noAttendeeReason
      executionOnly: $executionOnly
      precursorNotFinished: $precursorNotFinished
      postcursorNotFinished: $postcursorNotFinished
      focus: $focus
      isInJourney: $isInJourney
      clientCommunicationAttempts: $clientCommunicationAttempts
      reviewAction: $reviewAction
      isSubmittedFromOneview: $isSubmittedFromOneview
      sendForSignoff: $sendForSignoff
      juniorAdviserId: $juniorAdviserId
      adviceAction: $adviceAction
      signoffRejectedReason: $signoffRejectedReason
      signoffApprovalReason: $signoffApprovalReason
      padReviewerId: $padReviewerId
      adviserId: $adviserId
      giaHasLiquidityNeeds: $giaHasLiquidityNeeds
      pensionHasLiquidityNeeds: $pensionHasLiquidityNeeds
      accountSubmissionNeeded: $accountSubmissionNeeded
      reviewFor: $reviewFor
    ) {
      ok
      errors
      review {
        ...ReviewDetailsFragment
      }
    }
  }
  ${REVIEW_DETAILS_FRAGMENT}
`;
